import Image from 'next/image';
import { useTranslation } from 'react-i18next';

import { Box, Flex, Heading, Text, useBreakpointValue } from '@chakra-ui/react';

import { ButtonLink } from '~/components/button-link';
import { trackEvent } from '~/features/analytics/track-event';
import { HeroLayout } from '~/features/experiments/ab_plp_redesign_2025/components/hero-layout';
import { MessageIcon, RocketIcon } from '~/features/experiments/ab_plp_redesign_2025/icons';

export function EnterpriseOverviewHeroSection() {
  const { t } = useTranslation();
  const objectPositionDesktopImage = useBreakpointValue(
    {
      base: 'bottom left',
      lg: 'bottom left',
    },
    {
      fallback: 'lg',
    }
  );
  return (
    <HeroLayout
      backgroundImageOverlayGradient={{
        ['2xl']:
          'radial(circle at left, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0) 25%, rgba(0, 0, 0, 0) 70%, rgb(10, 11, 15) 92%)',
      }}
      backgroundImageSlot={
        <>
          <Box display={{ base: 'block', md: 'none' }} inset={0} position='absolute'>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src='/images/backgrounds/tech-workers-bg-mobile.png'
              style={{ objectFit: 'cover', objectPosition: 'left', minWidth: '496px' }}
              fill
              priority
            />
          </Box>
          <Box bottom='-2px' display={{ base: 'none', md: 'block' }} left={0} position='absolute' right={0} top={0}>
            <Image
              alt=''
              quality={100}
              sizes='100vw'
              src='/images/backgrounds/tech-workers-bg.png'
              style={{ objectFit: 'cover', objectPosition: objectPositionDesktopImage }}
              fill
              priority
            />
          </Box>
        </>
      }
      ctaSlot={
        <>
          <ButtonLink
            buttonProps={{
              leftIcon: <RocketIcon height='20px' width='20px' />,
              flex: {
                base: 1,
                md: 'unset',
              },
              onClick: () =>
                trackEvent({
                  name: 'Enterprise CTA Clicked',
                  type: 'click',
                  label: 'demo_skills_clicked',
                }),
            }}
            linkProps={{
              href: '/get-started/enterprise',
            }}
          >
            {t('enterprise.overview.cta.demoSkills')}
          </ButtonLink>
          <ButtonLink
            buttonProps={{
              leftIcon: <MessageIcon height='20px' width='20px' />,
              variant: 'outline',
              colorScheme: 'white',
              flex: {
                base: 1,
                md: 'unset',
              },
              onClick: () =>
                trackEvent({
                  name: 'Enterprise CTA Clicked',
                  type: 'click',
                  label: 'contact_us_clicked',
                }),
            }}
            linkProps={{
              href: '/get-started/enterprise',
            }}
          >
            {t('enterprise.overview.cta.contactUs')}
          </ButtonLink>
        </>
      }
      minHeight={{
        md: '674px',
      }}
      textAndCtaContainerProps={{
        gap: {
          base: 6,
          md: '48px',
        },
      }}
      textContentSlot={
        <Flex
          flexDirection='column'
          whiteSpace='pre-wrap'
          gap={{
            base: 4,
            md: 8,
          }}
          marginTop={{
            base: '192px',
            md: 'unset',
          }}
          maxWidth={{
            base: '357px',
            md: '496px',
          }}
        >
          <Heading
            as='h1'
            color='white'
            fontWeight='500'
            fontSize={{
              base: '3.25rem',
              md: '4.75rem',
            }}
            lineHeight={{
              base: '1.1',
              md: '1',
            }}
          >
            {t('enterprise.overview.hero.title')}
          </Heading>
          <Text color='gray.300' fontSize='lg' lineHeight='lg'>
            {t('enterprise.overview.hero.description')}
          </Text>
        </Flex>
      }
    />
  );
}
